import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, XCircle } from 'lucide-react';
import getApiUrl from './utils/getApiUrl';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Connexion en cours...');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Try to get session_id from multiple sources
        let sessionId = null;
        
        // 1. Check URL hash (fragment) - format: #session_id=xxx
        const hash = window.location.hash;
        if (hash && hash.length > 1) {
          const hashParams = new URLSearchParams(hash.substring(1));
          sessionId = hashParams.get('session_id');
        }
        
        // 2. Check query params - format: ?session_id=xxx
        if (!sessionId) {
          sessionId = searchParams.get('session_id');
        }
        
        // 3. Check if session_id is in the URL path somehow
        if (!sessionId) {
          const urlMatch = window.location.href.match(/session_id[=:]([^&\s#]+)/i);
          if (urlMatch) {
            sessionId = urlMatch[1];
          }
        }

        console.log('Full URL:', window.location.href);
        console.log('Hash:', hash);
        console.log('Session ID found:', sessionId);

        if (!sessionId) {
          setError('Session invalide. Le token de session n\'a pas été trouvé dans l\'URL.');
          return;
        }

        setStatus('Vérification de votre compte...');
        
        const apiUrl = getApiUrl();
        console.log('API URL:', apiUrl);

        // Send session_id to backend
        const formData = new FormData();
        formData.append('session_id', sessionId);

        const response = await fetch(`${apiUrl}/api/auth/session`, {
          method: 'POST',
          body: formData
        });

        console.log('Response status:', response.status);
        
        // Lire le body une seule fois (texte brut)
        const responseText = await response.text();
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('JSON parse error:', parseError, 'Raw:', responseText);
          setError(`Erreur serveur (${response.status}): ${responseText || 'Réponse invalide'}`);
          return;
        }
        
        if (!response.ok) {
          if (response.status === 403) {
            setError(data.detail || 'Votre email n\'est pas autorisé à accéder à cette application.');
          } else {
            setError(data.detail || `Erreur serveur (${response.status})`);
          }
          return;
        }
        
        console.log('Response data:', data);

        if (data.success) {
          setStatus('Connexion réussie !');
          // Store token in localStorage
          localStorage.setItem('auth_token', data.token);
          localStorage.setItem('auth_user', JSON.stringify(data.user));
          
          // Redirect to dashboard (on the same origin)
          setTimeout(() => {
            navigate('/', { state: { user: data.user }, replace: true });
          }, 500);
        } else {
          setError(data.detail || data.error || 'Erreur de connexion');
        }
      } catch (err) {
        console.error('Auth error:', err);
        // More informative error for network issues
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          setError('Erreur réseau. Vérifiez votre connexion et réessayez.');
        } else {
          setError(`Erreur de connexion: ${err.message}`);
        }
      }
    };

    processAuth();
  }, [navigate, searchParams]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/80 backdrop-blur-xl rounded-2xl border border-red-500/30 shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Accès refusé</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
        <p className="text-white text-lg">{status}</p>
      </div>
    </div>
  );
}
